import {Directive, HostBinding, inject, Input, OnChanges} from '@angular/core';
import {Params, Router, RouterLink, UrlTree} from '@angular/router';
import {Store} from '@ngrx/store';
import {take} from 'rxjs';
import {QueryParamsSelectors} from '@aviary/store/query-params/query-params.selectors';
import {buildValidatedRouterPathFrom} from '@aviary/store/router/router.helpers';
import {ROUTER_PATH_FOR} from '@aviary/injection-tokens/app-provided.tokens';
import {PathFor} from '@aviary/store/router/router.types';
import {hasValue, isDefined} from '@aviary/store/common/typing.helpers';
import {LocationStrategy} from '@angular/common';

@Directive({
  selector: 'a[mxNamedRouterLink]',
  hostDirectives: [RouterLink]
})
export class BaseNamedRouterLinkDirective implements OnChanges {
  /** The `name` property from some route's `data` object. */
  @Input() public mxNamedRouterLink!: string;

  /** The params needed to fill a raw route path's parameter variables.
   *
   * For example, if the route is `/something/:id/child/:childId`, the params should be an object like `{ id: 42, childId: 99 }`.
   */
  @Input() public mxNamedRouterLinkParams: Params = {};

  @Input() public mxQueryParams?: Params;

  @Input() public mxNamedRouterLinkEnabled: boolean = true;

  private readonly store = inject(Store);
  private readonly angularRouterLinkDirective = inject(RouterLink);
  private readonly pathFor: PathFor = inject(ROUTER_PATH_FOR);
  private readonly router: Router = inject(Router);
  private readonly locationStrategy: LocationStrategy = inject(LocationStrategy);

  @HostBinding('attr.href') public href: string | null = null;

  public ngOnChanges(): void {
    this.refreshLink();
  }

  /** Called by both our own ngOnChanges and wrapper directives. */
  public refreshLink(): void {
    if (!this.mxNamedRouterLinkEnabled) {
      this.angularRouterLinkDirective.routerLink = null;
      this.href = null;
      return;
    }

    if (isDefined(this.mxQueryParams)) {
      this.angularRouterLinkDirective.queryParams = {...this.mxQueryParams};
      this.updateRouterLink();
      return;
    }

    this.store.select(QueryParamsSelectors.cachedParamsFor(this.mxNamedRouterLink))
      .pipe(take(1))
      .subscribe(queryParams => {
        this.angularRouterLinkDirective.queryParams = {...queryParams};
        this.updateRouterLink();
      });
  }

  private updateRouterLink(): void {
    this.angularRouterLinkDirective.routerLink = buildValidatedRouterPathFrom(this.mxNamedRouterLink, this.mxNamedRouterLinkParams, this.pathFor);

    const urlTree: UrlTree | null = this.angularRouterLinkDirective.urlTree;
    this.href = hasValue(urlTree) ? this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(urlTree)) : null;
  }
}
